import { SxProps, Theme } from "@mui/material/styles";

const ACCENT_PURPLE = "#7B49C1";
const ACCENT_PURPLE_LIGHT = "rgba(123, 73, 193, 0.08)";
const ACCENT_PURPLE_LIGHT_DARK = "rgba(167, 139, 250, 0.16)";

const roleplayText = (theme: Theme) => ({
  title: theme.palette.mode === "dark" ? "#F3F4F6" : "#111827",
  label: theme.palette.mode === "dark" ? "#D1D5DB" : "#374151",
  value: theme.palette.mode === "dark" ? "#F9FAFB" : "#111827",
  placeholder: "#9CA3AF",
  muted: theme.palette.mode === "dark" ? "#9CA3AF" : "#6B7280",
  closeHover: theme.palette.mode === "dark" ? "#E5E7EB" : "#6B7280",
});

const purpleAccent = (theme: Theme) =>
  theme.palette.mode === "dark" ? "#C4B5FD" : ACCENT_PURPLE;

const purpleSurface = (theme: Theme) =>
  theme.palette.mode === "dark" ? ACCENT_PURPLE_LIGHT_DARK : ACCENT_PURPLE_LIGHT;

export const mainPopoverStyles: SxProps<Theme> = (theme: Theme) => ({
  "& .MuiPaper-root": {
    backgroundColor: theme.palette.footer.menuDropdown?.bgColor,
    mt: -13,
    borderRadius: "18px",
    padding: "16px 14px",
    overflowX: "hidden",
    overflowY: "auto",
    width: "360px",
    maxHeight: "min(80vh, 720px)",
    border:
      theme.palette.mode === "dark"
        ? "1px solid rgba(255, 255, 255, 0.1)"
        : "none",
  },
});

export const pageContainerStyles: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  width: "100%",
  gap: "14px",
};

export const cardHeaderRowStyles: SxProps = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "8px",
};

export const cardTitleStyles: SxProps<Theme> = (theme) => ({
  fontSize: "16px",
  fontWeight: 700,
  color: roleplayText(theme).title,
});

export const closeButtonStyles: SxProps<Theme> = (theme) => ({
  border: "none",
  background: "transparent",
  cursor: "pointer",
  color: "#9CA3AF",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "4px",
  borderRadius: "6px",
  "&:hover": {
    color: roleplayText(theme).closeHover,
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.08)"
        : "rgba(0,0,0,0.04)",
  },
});

export const fieldsContainerStyles: SxProps = {
  display: "flex",
  flexDirection: "column",
  gap: "12px",
};

export const fieldLabelStyles: SxProps<Theme> = (theme) => ({
  fontSize: "12px",
  fontWeight: 600,
  color: roleplayText(theme).label,
  mb: "6px",
});

export const dropdownFormControlStyles: SxProps = {
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
};

export const dropdownSelectStyles: SxProps<Theme> = (theme: Theme) => ({
  borderRadius: "10px",
  backgroundColor:
    theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "#FFFFFF",
  border: `1px solid ${
    theme.palette.mode === "dark" ? "rgba(255,255,255,0.14)" : "#E5E7EB"
  }`,
  "& .MuiSelect-select": {
    padding: "10px 12px",
    display: "flex",
    alignItems: "center",
  },
  "&:hover": {
    borderColor:
      theme.palette.mode === "dark" ? "rgba(255,255,255,0.24)" : "#D1D5DB",
  },
  "&.Mui-focused": {
    borderColor: ACCENT_PURPLE,
  },
  "&.Mui-disabled": {
    opacity: 0.6,
  },
});

export const getDropdownMenuPaperStyles = (theme: Theme): SxProps<Theme> => ({
  maxHeight: 280,
  borderRadius: "10px",
  mt: 0.5,
  backgroundColor:
    theme.palette.mode === "dark"
      ? theme.palette.background.paper
      : "#FFFFFF",
  border:
    theme.palette.mode === "dark"
      ? "1px solid rgba(255, 255, 255, 0.12)"
      : "1px solid #E5E7EB",
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 12px 32px rgba(0, 0, 0, 0.45)"
      : "0 8px 24px rgba(15, 23, 42, 0.12)",
  "& .MuiMenuItem-root:hover": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(167, 139, 250, 0.14)"
        : "rgba(123, 73, 193, 0.08)",
  },
  "& .MuiMenuItem-root.Mui-selected": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(167, 139, 250, 0.2)"
        : "rgba(123, 73, 193, 0.12)",
  },
});

export const dropdownValueRowStyles: SxProps = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  width: "100%",
  minWidth: 0,
};

export const getDropdownIconBoxStyles = (theme: Theme): SxProps<Theme> => ({
  width: 28,
  height: 28,
  borderRadius: "6px",
  backgroundColor: purpleSurface(theme),
  color: purpleAccent(theme),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
});

export const dropdownValueTextStyles: SxProps<Theme> = (theme) => ({
  fontSize: "14px",
  fontWeight: 500,
  color: roleplayText(theme).value,
  flex: 1,
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  textAlign: "left",
});

export const dropdownPlaceholderStyles: SxProps<Theme> = (theme) => ({
  fontSize: "14px",
  color: roleplayText(theme).placeholder,
  flex: 1,
  textAlign: "left",
});

export const dropdownChevronStyles = (theme: Theme): React.CSSProperties => ({
  width: 18,
  height: 18,
  color: roleplayText(theme).placeholder,
  flexShrink: 0,
  marginLeft: "auto",
});

export const dropdownMenuItemStyles: SxProps = {
  whiteSpace: "normal",
  alignItems: "flex-start",
  py: 1.25,
  height: "auto",
  minHeight: "auto",
};

export const dropdownMenuItemTextStyles: SxProps<Theme> = (theme) => ({
  fontSize: "14px",
  fontWeight: 500,
  color: roleplayText(theme).value,
  lineHeight: 1.3,
});

export const dropdownMenuItemDescriptionStyles: SxProps<Theme> = (theme) => ({
  fontSize: "12px",
  color: roleplayText(theme).muted,
  mt: 0.25,
  lineHeight: 1.35,
  whiteSpace: "normal",
  overflowWrap: "anywhere",
});

export const modeToggleRowStyles: SxProps = {
  display: "flex",
  alignItems: "stretch",
  gap: "10px",
  width: "100%",
};

// Icon stacks above the label so longer names (e.g. Voice Messaging) stay on one line.
export const getModeToggleButtonStyles =
  (isDisabled: boolean): SxProps<Theme> =>
  (theme: Theme) => {
    const isDark = theme.palette.mode === "dark";
    const accent = purpleAccent(theme);

    return {
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: "4px",
      padding: "10px 6px",
      borderRadius: "10px",
      border: `1px solid ${ACCENT_PURPLE}`,
      backgroundColor: purpleSurface(theme),
      color: accent,
      cursor: isDisabled ? "not-allowed" : "pointer",
      opacity: isDisabled ? 0.45 : 1,
      filter: isDisabled ? "grayscale(1)" : "none",
      fontFamily: "inherit",
      fontWeight: 500,
      fontSize: "12px",
      lineHeight: 1.2,
      outline: "none",
      transition: "background-color 0.15s, box-shadow 0.15s, transform 0.1s",
      "& .mode-toggle-icon": {
        color: accent,
        flexShrink: 0,
      },
      "&:hover": isDisabled
        ? {}
        : {
            backgroundColor: isDark
              ? "rgba(167, 139, 250, 0.24)"
              : "rgba(123, 73, 193, 0.16)",
          },
      "&:active": isDisabled
        ? {}
        : {
            backgroundColor: isDark
              ? "rgba(167, 139, 250, 0.32)"
              : "rgba(123, 73, 193, 0.22)",
            transform: "scale(0.99)",
          },
    };
  };

export const getPersonaAvatarFallbackStyles = (theme: Theme): SxProps<Theme> => ({
  width: 28,
  height: 28,
  borderRadius: "50%",
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: purpleSurface(theme),
  color: purpleAccent(theme),
});

export const loadingContainerStyles: SxProps = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "160px",
  gap: 2,
};

export const loaderContainerStyles: SxProps = {
  display: "flex",
  justifyContent: "center",
  py: 1,
};
